/**
 * What an Outlook turn tells the substrate (outlook-orachat-uplift s4,
 * design C7 and C54), passed to the shared panel as `turnContext`.
 *
 *  - `surface: "outlook"` — the substrate's Outlook layer keys off it.
 *  - `route: null` — Outlook has no pages; the panel then ships no route.
 *  - `email_context` — the open email, with exactly the keys the Outlook layer
 *    reads (workflows `outlook_profile.py` `email_context`): subject, sender
 *    name and address, plain-text body, recipients — and, in a compose window
 *    replying to a thread, `quotedThread` (s6). Never the HTML body. In the
 *    reading pane it also lists the attachments' METADATA only — name, type,
 *    size, kind and whether inline (s7, design C39): never their content,
 *    bytes or the Office attachment id.
 *  - `email_ref` — reading pane only, when the item has an id: which email
 *    this is (Internet message id — else the REST id, then the EWS id, as the
 *    tile keys it — REST id, mailbox, thread). The substrate's email tools read the email ONLY from it
 *    (s7, design C38). A compose window sends none.
 *  - `surface_mode: "compose"` only in a compose window; absent means the
 *    reading pane.
 */

import { resolveTriageMailbox } from "../features/sharedMailbox/triageMailbox";
import type { EmailContext } from "../hooks/useMailboxItem";

/** The Outlook surface id the substrate knows. */
export const OUTLOOK_SURFACE = "outlook";

/** The pane the add-in is open in: the reading pane or a compose window. */
export type OutlookPaneMode = "read" | "compose";

/** One attachment as the turn names it: metadata only (C39). */
export interface OutlookAttachmentMetadata {
  name: string;
  contentType?: string;
  size?: number;
  attachmentType?: string;
  isInline?: boolean;
}

export interface OutlookEmailContext {
  subject: string;
  senderName?: string;
  senderEmail?: string;
  body: string;
  recipients: string[];
  /** COMPOSE only: the thread quoted in the reply draft (`useComposeEmailContext`). */
  quotedThread?: string;
  /** READ only: the attachments' metadata (never their content or id). */
  attachments?: OutlookAttachmentMetadata[];
}

/** Which email the reading pane has open (C38) — the substrate validates it. */
export interface OutlookEmailRef {
  internet_message_id: string;
  rest_id?: string;
  mailbox_address?: string;
  conversation_id?: string;
}

export interface OutlookTurnContext {
  surface: typeof OUTLOOK_SURFACE;
  route: null;
  email_context?: OutlookEmailContext;
  email_ref?: OutlookEmailRef;
  surface_mode?: "compose";
  [key: string]: unknown;
}

/** The attachments' metadata: name, type, size, kind, inline — nothing else. */
function attachmentMetadata(email: EmailContext): OutlookAttachmentMetadata[] {
  return (email.attachments ?? [])
    .filter((a) => typeof a?.name === "string" && a.name.length > 0)
    .map((a) => ({
      name: a.name,
      ...(a.contentType ? { contentType: a.contentType } : {}),
      ...(typeof a.size === "number" ? { size: a.size } : {}),
      ...(a.attachmentType ? { attachmentType: a.attachmentType } : {}),
      ...(typeof a.isInline === "boolean" ? { isInline: a.isInline } : {}),
    }));
}

/**
 * The key `email_ref` names the open email by — the tile's identity chain
 * (`buildAnalyzeEmailPayload`): some Outlook hosts leave internetMessageId
 * empty, so fall back to the REST id, then the EWS id. "" when there is none.
 * A prepared workflow's brief is keyed by it (s8), so the pane compares it to
 * tell whether a card belongs to the email that is open now.
 */
export function emailRefMessageId(email: EmailContext | null | undefined): string {
  if (!email) return "";
  return email.internetMessageId || email.restId || email.emailId || "";
}

function emailRef(email: EmailContext): OutlookEmailRef | null {
  const imid = emailRefMessageId(email);
  if (!imid) return null;
  // The tile's mailbox key (`buildAnalyzeEmailPayload`, story 26727): the
  // detected SHARED mailbox when one exists, else the reader's own. The
  // substrate keys the analysis row and reads Graph with it (AD-12).
  const mailbox = resolveTriageMailbox(email) ?? email.mailboxAddress;
  return {
    internet_message_id: imid,
    ...(email.restId ? { rest_id: email.restId } : {}),
    ...(mailbox ? { mailbox_address: mailbox } : {}),
    ...(email.conversationId ? { conversation_id: email.conversationId } : {}),
  };
}

export function buildOutlookTurnContext({
  email,
  mode,
}: {
  email: EmailContext | null;
  mode: OutlookPaneMode;
}): OutlookTurnContext {
  const reading = mode === "read";
  const attachments = email && reading ? attachmentMetadata(email) : [];
  const ref = email && reading ? emailRef(email) : null;
  return {
    surface: OUTLOOK_SURFACE,
    route: null,
    ...(email
      ? {
          email_context: {
            subject: email.subject,
            senderName: email.senderName,
            senderEmail: email.senderEmail,
            body: email.body,
            recipients: email.recipients ?? [],
            ...(email.quotedThread ? { quotedThread: email.quotedThread } : {}),
            ...(attachments.length > 0 ? { attachments } : {}),
          },
        }
      : {}),
    ...(ref ? { email_ref: ref } : {}),
    ...(mode === "compose" ? { surface_mode: "compose" as const } : {}),
  };
}

/**
 * A done frame from a turn the add-in sent must say `surface: "outlook"`.
 * Anything else means the substrate answered some other surface's turn — a
 * named client error, never a silent wrong-surface reply.
 */
export function isWrongSurface(done: Record<string, unknown>): "wrong_surface" | null {
  return done.surface === OUTLOOK_SURFACE ? null : "wrong_surface";
}
